import { usePageTitle } from "@/hooks/use-page-title";
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, CheckCircle, XCircle, KeyRound, UserPlus } from "lucide-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Link, useSearch, useLocation } from "wouter";

const setPasswordSchema = z.object({
  newPassword: z.string().min(8, "Password must be at least 8 characters"),
  confirmPassword: z.string().min(1, "Please confirm your password"),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type SetPasswordForm = z.infer<typeof setPasswordSchema>;

export default function AcceptInvitePage() {
  usePageTitle("Accept Invitation");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const search = useSearch();
  const [, setLocation] = useLocation();
  const token = new URLSearchParams(search).get("token");
  const context = new URLSearchParams(search).get("context");

  const form = useForm<SetPasswordForm>({
    resolver: zodResolver(setPasswordSchema),
    defaultValues: {
      newPassword: "",
      confirmPassword: "",
    },
  });

  const { data: tokenValidation, isLoading: isValidating } = useQuery({
    queryKey: ["/api/auth/verify-invite-token", token],
    queryFn: async () => {
      const response = await fetch(`/api/auth/verify-invite-token?token=${token}`);
      return response.json();
    },
    enabled: !!token,
  });

  const role = tokenValidation?.user?.role;
  const isPortalUser = context === "portal" || role === "service_bureau";
  const loginHref = isPortalUser ? "/portal/login" : "/login";

  const acceptInviteMutation = useMutation({
    mutationFn: async (data: SetPasswordForm | null) => {
      const response = await apiRequest("POST", "/api/auth/accept-invite", {
        token,
        newPassword: data?.newPassword,
      });
      return response;
    },
    onSuccess: () => {
      setSuccess(true);
    },
    onError: (error: any) => {
      setError(error.message || "Failed to accept invite. Please try again.");
    },
  });

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setLocation(loginHref);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success, loginHref]);

  const handleSubmit = (data: SetPasswordForm) => {
    setError(null);
    acceptInviteMutation.mutate(data);
  };

  const handleExternalAccept = () => {
    setError(null);
    acceptInviteMutation.mutate(null);
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <div className="flex items-center justify-center mb-4">
              <XCircle className="h-12 w-12 text-red-600" />
            </div>
            <CardTitle className="text-2xl font-bold text-center">Invalid Link</CardTitle>
            <CardDescription className="text-center">
              This invite link is invalid. Please contact your administrator for a new invite.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link href={context === "portal" ? "/portal/login" : "/login"}>
              <Button className="w-full" data-testid="link-go-to-login">
                Go to Login
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <Card className="w-full max-w-md">
          <CardContent className="py-8">
            <div className="flex flex-col items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
              <p className="text-muted-foreground">Validating invite link...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (tokenValidation && !tokenValidation.valid) {
    const expiredLoginHref = context === "portal" ? "/portal/login" : "/login";
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <div className="flex items-center justify-center mb-4">
              <XCircle className="h-12 w-12 text-red-600" />
            </div>
            <CardTitle className="text-2xl font-bold text-center">Link Expired</CardTitle>
            <CardDescription className="text-center">
              This invite link has expired or is invalid. Please contact your administrator to resend the invite.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link href={expiredLoginHref}>
              <Button className="w-full" data-testid="link-go-to-login">
                Go to Login
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <div className="flex items-center justify-center mb-4">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
            <CardTitle className="text-2xl font-bold text-center">Welcome!</CardTitle>
            <CardDescription className="text-center">
              {isPortalUser
                ? "Your account has been set up. Redirecting you to the portal login..."
                : "Your account has been set up successfully. Redirecting you to login..."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link href={loginHref}>
              <Button className="w-full" data-testid="link-go-to-login">
                {isPortalUser ? "Go to Portal Login" : "Go to Login"}
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isInternalUser = tokenValidation?.user?.authType === 'internal';
  const userName = tokenValidation?.user?.firstName || 'User';

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex items-center justify-center mb-4">
            {isInternalUser ? (
              <KeyRound className="h-8 w-8 text-blue-600 dark:text-blue-400" />
            ) : (
              <UserPlus className="h-8 w-8 text-blue-600 dark:text-blue-400" />
            )}
          </div>
          <CardTitle className="text-2xl font-bold text-center">
            Welcome, {userName}!
          </CardTitle>
          <CardDescription className="text-center">
            {isInternalUser
              ? "Set up your password to complete your account setup"
              : "Click below to activate your account"}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {isInternalUser ? (
            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="newPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Password</FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          placeholder="Enter your password"
                          data-testid="input-new-password"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Confirm Password</FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          placeholder="Confirm your password"
                          data-testid="input-confirm-password"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button
                  type="submit"
                  className="w-full"
                  disabled={acceptInviteMutation.isPending}
                  data-testid="button-set-password"
                >
                  {acceptInviteMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Setting Up...
                    </>
                  ) : (
                    "Complete Setup"
                  )}
                </Button>
              </form>
            </Form>
          ) : (
            <Button
              onClick={handleExternalAccept}
              className="w-full"
              disabled={acceptInviteMutation.isPending}
              data-testid="button-activate-account"
            >
              {acceptInviteMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Activating...
                </>
              ) : (
                "Activate My Account"
              )}
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
